/**
 * Wishlist Controller
 *
 * Custom element that manages wishlist state using localStorage.
 * Uses global event delegation to handle all wishlist button clicks.
 *
 * Architecture:
 * - Custom element wrapper for lifecycle management
 * - Event delegation for all wishlist actions
 * - localStorage for persistent storage
 * - Shopify event system for state changes
 *
 * Features:
 * - Add/remove/toggle products in wishlist
 * - Clear entire wishlist
 * - Persistent storage with localStorage
 * - Product count tracking
 * - Button state management (added/idle)
 * - Load products from Shopify API
 *
 * Events Emitted:
 * - wishlist:initialized { items, count } - On controller initialization
 * - wishlist:added { productId, items, count } - When product is added
 * - wishlist:removed { productId, items, count } - When product is removed
 * - wishlist:updated { items, count, action, productId } - On any state change
 * - wishlist:cleared - When wishlist is cleared
 *
 * Data Attributes Required:
 * - [data-wishlist-toggle="{id}"] - Toggle button (id optional if inside product-controller)
 * - [data-wishlist-add="{id}"] - Add button (id optional if inside product-controller)
 * - [data-wishlist-remove="{id}"] - Remove button (id optional if inside product-controller)
 * - [data-wishlist-clear] - Clear button (removes all products from wishlist)
 * - [data-btn-type="icon"] - Optional: prevents text content updates for icon-only buttons
 *
 * Product ID Resolution:
 * The controller will look for product ID in this order:
 * 1. Button's data-wishlist-toggle/add/remove attribute value
 * 2. Button's data-product-id attribute
 * 3. Parent product-controller's data-product-id attribute
 *
 * Examples:
 * - With ID: <button data-wishlist-toggle="123456789">Add to Wishlist</button>
 * - Without ID (inside product-controller): <button data-wishlist-toggle>Add to Wishlist</button>
 *
 * Button States:
 * - data-state="idle" - Product not in wishlist
 * - data-state="added" - Product in wishlist
 *
 * Storage:
 * - Key: 'stillmold-wishlist'
 * - Format: JSON array of product ID strings
 * - Example: ["123456789", "987654321"]
 *
 * Public API:
 * - has(productId) - Check if product is in wishlist
 * - getItems() - Get array of all product IDs
 * - count - Get number of items in wishlist
 * - loadProducts() - Fetch full product data from Shopify
 *
 * Locale Strings Used:
 * - window.themeStrings.wishlist.add - "Add to Wishlist" button text
 * - window.themeStrings.wishlist.remove - "Remove from Wishlist" button text
 *
 * Usage:
 * Place <wishlist-controller></wishlist-controller> in your theme layout.
 * All wishlist buttons will be automatically handled via event delegation.
 */

class WishlistController extends HTMLElement {
  constructor() {
    super();

    this._items = new Set();
    this._storageKey = "stillmold-wishlist";

    // Save bound references for proper cleanup
    this._boundHandleClick = this._handleDelegatedClick.bind(this);
  }

  connectedCallback() {
    this._loadFromStorage();

    // Event delegation
    document.addEventListener("click", this._boundHandleClick);

    Shopify.emitEvent("wishlist:initialized", {
      items: this.getItems(),
      count: this.count,
    });
  }

  disconnectedCallback() {
    document.removeEventListener("click", this._boundHandleClick);
  }

  /* ===========================================
   * EVENT DELEGATION
   * =========================================== */

  _handleDelegatedClick(e) {
    const target = e.target.closest("button, a");
    if (!target) return;

    // Toggle button
    if (target.matches("[data-wishlist-toggle]")) {
      e.preventDefault();
      const productId = this._getProductId(target);
      if (productId) {
        this._toggle(productId);
      }
      return;
    }

    // Add button
    if (target.matches("[data-wishlist-add]")) {
      e.preventDefault();
      const productId = this._getProductId(target);
      if (productId) {
        this._add(productId);
      }
      return;
    }

    // Remove button
    if (target.matches("[data-wishlist-remove]")) {
      e.preventDefault();
      const productId = this._getProductId(target);
      if (productId) {
        this._remove(productId);
      }
      return;
    }

    // Clear all button
    if (target.matches("[data-wishlist-clear]")) {
      e.preventDefault();
      this._clear();
      return;
    }
  }

  /* ===========================================
   * PRIVATE API
   * =========================================== */

  _getProductId(button) {
    // First try to get from button's data attributes
    const buttonProductId =
      button.dataset.productId ||
      button.dataset.wishlistToggle ||
      button.dataset.wishlistAdd ||
      button.dataset.wishlistRemove;

    if (buttonProductId) {
      return buttonProductId;
    }

    // Fallback: get from parent product-controller
    const productController = button.closest("product-controller");
    if (productController) {
      return productController.dataset.productId;
    }

    return null;
  }

  /* ===========================================
   * WISHLIST OPERATIONS
   * =========================================== */

  _add(productId) {
    const id = String(productId);

    if (this._items.has(id)) {
      return false;
    }

    this._items.add(id);
    this._saveToStorage();
    this._updateButton(id, true);

    Shopify.emitEvent("wishlist:added", {
      productId: id,
      items: this.getItems(),
      count: this.count,
    });

    Shopify.emitEvent("wishlist:updated", {
      items: this.getItems(),
      count: this.count,
      action: "add",
      productId: id,
    });

    return true;
  }

  _remove(productId) {
    const id = String(productId);

    if (!this._items.has(id)) {
      return false;
    }

    this._items.delete(id);
    this._saveToStorage();
    this._updateButton(id, false);

    Shopify.emitEvent("wishlist:removed", {
      productId: id,
      items: this.getItems(),
      count: this.count,
    });

    Shopify.emitEvent("wishlist:updated", {
      items: this.getItems(),
      count: this.count,
      action: "remove",
      productId: id,
    });

    return true;
  }

  _toggle(productId) {
    const id = String(productId);

    if (this._items.has(id)) {
      this._remove(id);
      return false;
    } else {
      this._add(id);
      return true;
    }
  }

  _clear() {
    this._items.clear();
    this._saveToStorage();
    this._syncButtonStates();

    Shopify.emitEvent("wishlist:cleared");

    Shopify.emitEvent("wishlist:updated", {
      items: [],
      count: 0,
      action: "clear",
    });
  }

  /* ===========================================
   * UI UPDATES
   * =========================================== */
  _updateButtonElement(btn, state) {
    btn.dataset.state = state;

    if (btn.dataset.btnType !== "icon") {
      switch (state) {
        case "added":
          btn.textContent =
            window.themeStrings?.wishlist?.remove || "Remove from Wishlist";
          break;

        case "idle":
          btn.textContent =
            window.themeStrings?.wishlist?.add || "Add to Wishlist";
          break;
      }
    }
  }

  _updateButton(productId, isInWishlist) {
    const id = String(productId);
    const state = isInWishlist ? "added" : "idle";

    // Query all wishlist buttons
    const buttons = document.querySelectorAll(
      "[data-wishlist-toggle], [data-wishlist-add], [data-wishlist-remove]",
    );

    // Update buttons that match this product ID
    buttons.forEach((btn) => {
      const btnProductId = this._getProductId(btn);
      if (btnProductId === id) {
        this._updateButtonElement(btn, state);
      }
    });
  }

  _syncButtonStates() {
    const buttons = document.querySelectorAll(
      "[data-wishlist-toggle], [data-wishlist-add], [data-wishlist-remove]",
    );

    buttons.forEach((btn) => {
      const productId = this._getProductId(btn);

      if (productId) {
        const state = this.has(productId) ? "added" : "idle";
        this._updateButtonElement(btn, state);
      }
    });
  }

  /* ===========================================
   * UTILITIES
   * =========================================== */

  has(productId) {
    return this._items.has(String(productId));
  }

  getItems() {
    return Array.from(this._items);
  }

  get count() {
    return this._items.size;
  }

  /* ===========================================
   * STORAGE MANAGEMENT
   * =========================================== */

  _loadFromStorage() {
    try {
      const stored = localStorage.getItem(this._storageKey);

      if (stored) {
        const items = JSON.parse(stored);

        if (Array.isArray(items)) {
          this._items = new Set(items.map(String));
        }
      }
    } catch (error) {
      console.error("Failed to load wishlist from storage:", error);
      this._items = new Set();
    }
  }

  _saveToStorage() {
    try {
      const items = this.getItems();
      localStorage.setItem(this._storageKey, JSON.stringify(items));
    } catch (error) {
      console.error("Failed to save wishlist to storage:", error);
    }
  }

  /* ===========================================
   * OTHER API
   * =========================================== */

  async loadProducts() {
    const rawWishlistIds = this.getItems();
    if (!rawWishlistIds.length) return [];
    try {
      const wishlistIds = rawWishlistIds.map((id) => "id:" + id).join(" OR ");
      const result = await fetch(
        `/search?types=product&q=${wishlistIds}&section_id=wishlist-result`,
      ).then((res) => res.text());

      // Remove stale product IDs (deleted by admin) from localStorage
      const doc = new DOMParser().parseFromString(result, "text/html");
      const returnedIds = new Set(
        Array.from(doc.querySelectorAll("[data-product-id]")).map((el) =>
          String(el.dataset.productId),
        ),
      );

      const staleIds = rawWishlistIds.filter((id) => !returnedIds.has(id));
      if (staleIds.length) {
        staleIds.forEach((id) => this._items.delete(id));
        this._saveToStorage();
        Shopify.emitEvent("wishlist:updated", {
          items: this.getItems(),
          count: this.count,
          action: "cleanup",
        });
      }

      return result;
    } catch (err) {
      console.error("Wishlist load failed", err);
      return [];
    }
  }
}

customElements.define("wishlist-controller", WishlistController);
